import { useCallback, useMemo, useRef, useState } from 'react';

import DatePicker from '../../../../components/DatePicker';
import { Drawer } from '../../../../components/Drawer';
import Dropdown from '../../../../components/Dropdown';
import { FormList, FormListFieldError, FormListItem } from '../../../../components/FormList';
import { HelpDrawerLink } from '../../../../components/HelpDrawerLink';
import TextField from '../../../../components/TextField';
import { WorkflowSubStepProps } from '../../config';

export type RetiredPayType = 'retirement-pay' | 'pension' | 'other';

export interface RetiredPayEntry {
  otherPayType: string;
  payType: RetiredPayType | '';
  receiptEndDate: string;
  receiptStartDate: string;
  stillReceivingPay: boolean | null;
}

export interface RetirementPayFormData {
  isAddingEntry?: boolean;
  hasRetirementPay: boolean | null;
  draftRetiredPay: RetiredPayEntry;
  retiredPays: FormListItem<RetiredPayEntry>[];
}

const yesNoOptions = [
  { label: '- Select an option -', value: null },
  { label: 'Yes', value: true },
  { label: 'No', value: false },
];

const payTypeOptions = [
  { label: '- Select an option -', value: '' },
  { label: 'Retirement Pay', value: 'retirement-pay' },
  { label: 'Pension', value: 'pension' },
  {
    label: 'Other retired pay based on military, Federal civilian, or District of Columbia Government service',
    value: 'other',
  },
];

const getEmptyRetiredPay = (): RetiredPayEntry => ({
  otherPayType: '',
  payType: '',
  receiptEndDate: '',
  receiptStartDate: '',
  stillReceivingPay: null,
});

function RetirementPayPage({ data, fieldErrors, updateData }: WorkflowSubStepProps<RetirementPayFormData>) {
  const [isHelpDrawerOpen, setIsHelpDrawerOpen] = useState(false);
  const baseTestId = 'retirement-pay-page';
  const today = useMemo(() => new Date(), []);
  const minDate = useMemo(() => {
    const date = new Date();
    date.setFullYear(date.getFullYear() - 100);

    return date;
  }, []);

  const handleRetirementPayChange = (value: boolean | null) => {
    if (value !== true) {
      updateData({
        ...data,
        draftRetiredPay: getEmptyRetiredPay(),
        hasRetirementPay: value,
        isAddingEntry: false,
        retiredPays: [],
      });
    } else {
      updateData({
        ...data,
        hasRetirementPay: value,
      });
    }
  };

  const pendingItemsRef = useRef<FormListItem<RetiredPayEntry>[] | null>(null);

  const handleItemsChange = useCallback(
    (items: FormListItem<RetiredPayEntry>[]) => {
      pendingItemsRef.current = items;
      updateData({
        ...data,
        retiredPays: items,
      });
    },
    [data, updateData],
  );

  const handleDraftChange = useCallback(
    (draft: RetiredPayEntry, isDraftActive?: boolean) => {
      const itemsToUse = pendingItemsRef.current !== null ? pendingItemsRef.current : data.retiredPays;
      pendingItemsRef.current = null;

      updateData({
        ...data,
        draftRetiredPay: draft,
        isAddingEntry: isDraftActive ?? data.isAddingEntry,
        retiredPays: itemsToUse,
      });
    },
    [data, updateData],
  );

  const handleDraftActiveChange = useCallback(
    (isActive: boolean) => updateData({ ...data, isAddingEntry: isActive }),
    [data, updateData],
  );

  const validateRetiredPay = useCallback((item: RetiredPayEntry): FormListFieldError => {
    const errors: FormListFieldError = {};

    if (!item.payType) {
      errors.payType = 'Type of pay is required';
    }
    if (item.payType === 'other' && !item.otherPayType) {
      errors.otherPayType = 'Other pay type is required';
    }
    if (item.stillReceivingPay === null || item.stillReceivingPay === undefined) {
      errors.stillReceivingPay = 'You must select whether you still receive this pay';
    }
    if (item.stillReceivingPay === true && !item.receiptStartDate) {
      errors.receiptStartDate = 'Receipt start date is required';
    }
    if (item.stillReceivingPay === false && !item.receiptEndDate) {
      errors.receiptEndDate = 'Receipt end date is required';
    }

    return errors;
  }, []);

  const isDraftEmpty = useCallback((draft: RetiredPayEntry): boolean => {
    return (
      !draft.payType &&
      !draft.otherPayType &&
      draft.stillReceivingPay === null &&
      !draft.receiptStartDate &&
      !draft.receiptEndDate
    );
  }, []);

  const renderEditForm = useCallback(
    (item: RetiredPayEntry, onChange: (item: RetiredPayEntry) => void, errors: FormListFieldError) => {
      const handleFieldChange = <K extends keyof RetiredPayEntry>(field: K, value: RetiredPayEntry[K]) => {
        const nextItem = {
          ...item,
          [field]: value,
        };

        if (field === 'payType' && value !== 'other') {
          nextItem.otherPayType = '';
        }
        if (field === 'stillReceivingPay') {
          nextItem.receiptStartDate = value === true ? nextItem.receiptStartDate : '';
          nextItem.receiptEndDate = value === false ? nextItem.receiptEndDate : '';
        }

        onChange(nextItem);
      };

      return (
        <div
          className='form-list__nested-form'
          data-testid={`${baseTestId}-entry-form`}>
          <div
            className='ds-u-margin-bottom--3'
            data-testid={`${baseTestId}-entry-pay-type`}>
            <Dropdown
              required
              data-testid={`${baseTestId}-entry-pay-type-dropdown`}
              errorMessage={errors.payType}
              label='What type of pay do you receive?'
              name='payType'
              options={payTypeOptions}
              tooltipText='Select the type of retirement pay, pension, or other retired pay you receive.'
              value={item.payType || ''}
              onChange={(value) => handleFieldChange('payType', value as RetiredPayEntry['payType'])}
            />
          </div>

          {item.payType === 'other' && (
            <div
              className='ds-u-margin-bottom--3'
              data-testid={`${baseTestId}-entry-other-pay-type`}>
              <TextField
                required
                data-testid={`${baseTestId}-entry-other-pay-type-field`}
                errorMessage={errors.otherPayType}
                label='If other, enter the type of pay you receive'
                maxLength={200}
                name='otherPayType'
                placeholder='Name of the other pay type'
                tooltipText='Enter the type of other retired pay you receive.'
                value={item.otherPayType || ''}
                onChange={(event) => handleFieldChange('otherPayType', event.target.value)}
              />
            </div>
          )}

          {item.payType && (
            <div
              className='ds-u-margin-bottom--3'
              data-testid={`${baseTestId}-entry-still-receiving-pay`}>
              <Dropdown
                required
                data-testid={`${baseTestId}-entry-still-receiving-pay-dropdown`}
                errorMessage={errors.stillReceivingPay}
                label='Do you still receive this pay?'
                name='stillReceivingPay'
                options={yesNoOptions}
                tooltipText='Select whether you currently receive this pay.'
                value={item.stillReceivingPay}
                onChange={(value) => handleFieldChange('stillReceivingPay', value)}
              />
            </div>
          )}

          {item.stillReceivingPay === true && (
            <div
              className='ds-u-margin-bottom--3'
              data-testid={`${baseTestId}-entry-receipt-start-date`}>
              <DatePicker
                required
                data-testid={`${baseTestId}-entry-receipt-start-date-picker`}
                errorMessage={errors.receiptStartDate}
                fromDate={minDate}
                id='receiptStartDate'
                label='Receipt start date'
                name='receiptStartDate'
                toDate={today}
                tooltipText='Enter the date you started receiving this pay.'
                value={item.receiptStartDate || ''}
                onDateChange={(date, formattedDate) => handleFieldChange('receiptStartDate', formattedDate || '')}
              />
            </div>
          )}

          {item.stillReceivingPay === false && (
            <div
              className='ds-u-margin-bottom--3'
              data-testid={`${baseTestId}-entry-receipt-end-date`}>
              <DatePicker
                required
                data-testid={`${baseTestId}-entry-receipt-end-date-picker`}
                errorMessage={errors.receiptEndDate}
                fromDate={minDate}
                id='receiptEndDate'
                label='Receipt end date'
                name='receiptEndDate'
                toDate={today}
                tooltipText='Enter the date you stopped receiving this pay.'
                value={item.receiptEndDate || ''}
                onDateChange={(date, formattedDate) => handleFieldChange('receiptEndDate', formattedDate || '')}
              />
            </div>
          )}
        </div>
      );
    },
    [minDate, today],
  );

  const renderItemSummary = useCallback((item: RetiredPayEntry) => {
    const payTypeLabel = payTypeOptions.find((option) => option.value === item.payType)?.label ?? 'Retired Pay';
    const title = item.payType === 'other' && item.otherPayType ? item.otherPayType : payTypeLabel;
    const status = item.stillReceivingPay ? 'Still receiving pay' : 'No longer receiving pay';
    const date = item.stillReceivingPay ? item.receiptStartDate : item.receiptEndDate;
    const dateLabel = item.stillReceivingPay ? 'Receipt start date' : 'Receipt end date';

    return {
      details: date ? [status, `${dateLabel}: ${date}`] : status,
      title,
    };
  }, []);

  const renderReadOnlyItem = useCallback((item: RetiredPayEntry) => {
    const payTypeLabel = payTypeOptions.find((option) => option.value === item.payType)?.label ?? '';

    return (
      <div className='form-list__nested-form'>
        <div className='form-list__readonly-field'>
          <span className='form-list__readonly-label'>Type of pay</span>
          <span className='form-list__readonly-value'>
            {item.payType === 'other' ? item.otherPayType : payTypeLabel}
          </span>
        </div>
        <div className='form-list__readonly-field'>
          <span className='form-list__readonly-label'>Still receiving pay</span>
          <span className='form-list__readonly-value'>{item.stillReceivingPay ? 'Yes' : 'No'}</span>
        </div>
        <div className='form-list__readonly-field'>
          <span className='form-list__readonly-label'>
            {item.stillReceivingPay ? 'Receipt start date' : 'Receipt end date'}
          </span>
          <span className='form-list__readonly-value'>
            {item.stillReceivingPay ? item.receiptStartDate : item.receiptEndDate}
          </span>
        </div>
      </div>
    );
  }, []);

  const draftFieldErrors: FormListFieldError = {
    otherPayType: fieldErrors?.['draftRetiredPay.otherPayType'],
    payType: fieldErrors?.['draftRetiredPay.payType'],
    receiptEndDate: fieldErrors?.['draftRetiredPay.receiptEndDate'],
    receiptStartDate: fieldErrors?.['draftRetiredPay.receiptStartDate'],
    stillReceivingPay: fieldErrors?.['draftRetiredPay.stillReceivingPay'],
  };

  return (
    <div data-testid={baseTestId}>
      <div
        className='ds-u-margin-bottom--3'
        data-testid={`${baseTestId}-has-retirement-pay`}>
        <Dropdown
          required
          data-testid={`${baseTestId}-has-retirement-pay-dropdown`}
          errorMessage={fieldErrors?.hasRetirementPay}
          label='Do you receive, or have you ever applied for, retirement pay, pension, or other retired pay based on military, Federal civilian, or District of Columbia Government service?'
          name='hasRetirementPay'
          options={yesNoOptions}
          tooltipText='This includes military retirement pay, Federal civilian retirement (FERS or CSRS), District of Columbia Government retirement, or military disability retirement.'
          value={data.hasRetirementPay}
          onChange={handleRetirementPayChange}
        />
      </div>

      {data.hasRetirementPay === true && (
        <div
          className='ds-u-margin-bottom--3'
          data-testid={`${baseTestId}-entries`}>
          <FormList<RetiredPayEntry>
            hideLabel
            addLinkText='Add Another Retired Pay'
            data-testid={`${baseTestId}-entries-list`}
            draftItem={data.draftRetiredPay}
            fieldErrors={draftFieldErrors}
            getEmptyItem={getEmptyRetiredPay}
            isDraftActive={data.isAddingEntry}
            isDraftEmpty={isDraftEmpty}
            items={data.retiredPays}
            label='Retired pay details'
            renderEditForm={renderEditForm}
            renderItemSummary={renderItemSummary}
            renderReadOnlyItem={renderReadOnlyItem}
            tooltipText='Provide details for each retirement pay, pension, or other retired pay you receive.'
            validateItem={validateRetiredPay}
            onDraftActiveChange={handleDraftActiveChange}
            onDraftChange={handleDraftChange}
            onItemsChange={handleItemsChange}
          />
        </div>
      )}

      <div
        className='ds-u-border-top--2 ds-u-padding-top--3 ds-u-margin-top--4 field-max-width'
        data-testid={`${baseTestId}-help`}>
        <HelpDrawerLink
          data-testid={`${baseTestId}-help-link`}
          onClick={() => setIsHelpDrawerOpen(true)}
        />
      </div>

      <Drawer
        data-testid={`${baseTestId}-drawer`}
        heading='Help: Retirement Pay'
        isOpen={isHelpDrawerOpen}
        onClose={() => setIsHelpDrawerOpen(false)}>
        <div className='ds-u-padding--3'>
          <h3 className='ds-u-margin-top--0'>Get Help with Retired Pay Details</h3>
          <p>
            This section is about retirement money, pensions, or other benefits you get when you retire from government
            jobs. This is required for the Declaration of Federal Employment (OF-306).
          </p>

          <h4 className='ds-u-margin-top--3'>What to Include</h4>
          <p>Answer &quot;Yes&quot; if you receive or have applied for:</p>
          <ul>
            <li>Military retirement pay</li>
            <li>Federal civilian retirement (FERS or CSRS)</li>
            <li>District of Columbia Government retirement</li>
            <li>Military disability retirement</li>
          </ul>

          <h4 className='ds-u-margin-top--3'>Why This Information is Needed</h4>
          <p>
            We need this data to follow laws about getting paid from two sources. This will help us process your
            application properly.
          </p>
        </div>
      </Drawer>
    </div>
  );
}

export default RetirementPayPage;
